import { ToastType } from '@components/toast';
import { SystemAlertGetResponse } from '@lio/api-types';
import { alertsQueryKeys } from '@services/alert/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useDeleteSystemAlert = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutationFn = (systemAlertId: string) => {
    const url = `system-management/system-alert/${systemAlertId}`;
    return client.delete(url);
  };

  const result = useMutation({
    mutationFn,
    onMutate: async (systemAlertIdToDelete: string) => {
      queryClient.invalidateQueries({ queryKey: [...alertsQueryKeys.all] });
      const previousAlerts = queryClient.getQueryData<SystemAlertGetResponse[]>(alertsQueryKeys.searchAlerts());
      queryClient.setQueryData(alertsQueryKeys.searchAlerts(), (old: any = []) =>
        old.filter((alert: SystemAlertGetResponse) => alert.id !== systemAlertIdToDelete),
      );
      return { previousAlerts };
    },
    onError: async (error, _, context: any) => {
      if (context?.previousAlerts) {
        queryClient.setQueryData(alertsQueryKeys.searchAlerts(), context.previousAlerts);
      }
      const message = await getApiErrorMessage(error, 'Failed to delete the system alert.');
      enqueueSnackbar(message, {
        variant: ToastType.Error,
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('System Alert Successfully Deleted', {
        variant: ToastType.Success,
      });
    },
  });
  return { ...result, deleteSystemAlertAsync: result.mutateAsync };
};
